import { type CommonAction, success } from "@iflyrpa/share";
import { z } from "zod";
import type { ActiomCommonParams, AxiosProxyParams } from "../../../types";
import { ActionCommonParamsSchema } from "../../../types";
import { Http } from "../../../utils/http";

// 环境日志采样：基于时间，每5秒最多记录一次
let lastEnvLogTime = 0;
const ENV_LOG_INTERVAL_MS = 5000; // 5秒

export const SessionCheckResultSchema = z.object({ isValidSession: z.boolean() });
export type sessionCheckResult = z.infer<typeof SessionCheckResultSchema>;

export const WxBjhSessionParamsSchema = ActionCommonParamsSchema.extend({
    token: z.union([z.string(), z.number()]),
});
export type WxBjhSessionParams = z.infer<typeof WxBjhSessionParamsSchema>;

export type ttSessionCheck = CommonAction<ActiomCommonParams, sessionCheckResult>;
export type wxSessionCheck = CommonAction<WxBjhSessionParams, sessionCheckResult>;
export type xhsSessionCheck = CommonAction<ActiomCommonParams, sessionCheckResult>;
export type bjhSessionCheck = CommonAction<WxBjhSessionParams, sessionCheckResult>;
export type dySessionCheck = CommonAction<ActiomCommonParams, sessionCheckResult>;
export type shipinhaoSessionCheck = CommonAction<ActiomCommonParams, sessionCheckResult>;

export const TTSessionCheck: ttSessionCheck = async (_task, params) => {
    const http = new Http({
        headers: {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            referer: "https://mp.toutiao.com/profile_v4/graphic/publish",
        },
    });
    const res = await http.api({
        method: "get",
        url: "https://mp.toutiao.com/mp/agw/creator_center/user_info",
        defaultErrorMsg: "头条号登录状态失效。",
    });

    const isSuccess = res.code === 0;
    const message = "头条账号有效性检测成功";
    const data = isSuccess ? { isValidSession: true } : { isValidSession: false };

    return success(data, message);
};

export const WxSessionCheck: wxSessionCheck = async (_task, params) => {
    const http = new Http({
        headers: {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        },
    });
    const fingerprint = "4695500bc93ab4ce8fb2692da6564e04";

    const res = await http.api<{ base_resp: { ret: number } }>({
        method: "get",
        url: "https://mp.weixin.qq.com/cgi-bin/appmsgpublish",
        params: {
            fingerprint,
            sub: "list",
            begin: 0,
            count: 10,
            query: "",
            type: 101_1_102_103,
            show_type: "",
            free_publish_type: 1_102_103,
            sub_action: "list_ex",
            search_card: 0,
            token: params.token,
            lang: "zh_CN",
            f: "json",
            ajax: 1,
        },
    });

    return success(
        res.base_resp.ret === 0 ? { isValidSession: true } : { isValidSession: false },
        "微信账号有效性检测完成！",
    );
};

export const XhsSessionCheck: xhsSessionCheck = async (_task, params) => {
    // 🔍 环境诊断日志 - 基于时间采样，每5秒最多记录一次
    const now = Date.now();
    const shouldLogEnv = now - lastEnvLogTime >= ENV_LOG_INTERVAL_MS;

    if (shouldLogEnv) {
        lastEnvLogTime = now;
        _task.logger?.debug("[XhsSessionCheck] 环境信息", {
            processType: process.type,
            pid: process.pid,
            nodeVersion: process.version,
            platform: process.platform,
            memoryUsage: {
                heapUsed: `${Math.round(process.memoryUsage().heapUsed / 1024 / 1024)} MB`,
                heapTotal: `${Math.round(process.memoryUsage().heapTotal / 1024 / 1024)} MB`,
            },
            accountId: params.accountId,
            hasProxy: !!params.proxyLoc,
        });
    }

    const check = { isValidSession: false, isValidWebSession: false };

    const a1Cookie = params.cookies.find((it) => it.name === "a1")?.value;
    if (!a1Cookie) {
        return { code: 414, message: "账号数据异常，请重新绑定账号后重试。", data: check };
    }

    const headers = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        referer: "https://creator.xiaohongshu.com/new/home?source=official",
    };

    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.accountId, "xiaohongshu"];
    const http = new Http(...args);
    http.addResponseInterceptor((response) => {
        if (response?.status === 401)
            return {
                code: response.data.code,
                message: response.data.msg,
                data: response.data.data,
            };
    });

    // creator 站点会话探测接口，两个状态位都由它决定。
    //
    // 必须用 personal_info，不能用 /api/galaxy/v2/creator/datacenter/account/base：
    // 后者的鉴权策略更松，galaxy_creator_session_id（约 30 天有效期）过期后仍返回 200，
    // 导致会话检测报「有效」，而实际依赖 personal_info 的 SearchAccountInfo 必然 401。
    // 由于 web_session 有效期长达一年，这种漏报状态可以持续将近 11 个月。
    // 探测点必须与业务真实依赖的接口一致，否则检测没有意义。
    //
    // personal_info 不需要 xs 签名，成功返回 {code: 0, ...}，
    // 会话过期时返回 HTTP 401 + {code: -1, msg: "登录已过期", result: -100}。
    const loginBasePath = "/api/galaxy/creator/home/personal_info";

    const baseInfoStart = Date.now();
    const baseInfo = await http
        .api<{ code: number; msg: string }>(
            {
                method: "get",
                baseURL: "https://creator.xiaohongshu.com",
                url: loginBasePath,
            },
            { retries: 3, retryDelay: 300, timeout: 30000 },
        )
        .catch((e) => {
            // 时钟漂移检测：对比客户端时间与小红书服务端返回的 Date 头。
            // 本接口不签名，理论上不受 x-t 时效影响，但客户端时钟异常往往伴随其他会话问题，
            // 保留该指标便于区分「真的会话失效」与「本机时间不对」。
            const clientTimestamp = Date.now();
            const serverDate = (e?.extra as { serverDate?: string })?.serverDate;
            const serverTimestamp = serverDate ? new Date(serverDate).getTime() : null;
            const timeDriftMs = serverTimestamp ? clientTimestamp - serverTimestamp : null;

            _task.logger?.warn("[XhsSessionCheck] creator personal_info 接口请求失败", {
                accountId: params.accountId,
                duration: Date.now() - baseInfoStart,
                code: e?.code,
                message: e?.message,
                data: e?.data,
                extra: e?.extra,
                proxyInfo: http.proxyInfo,
                clientTimestamp,
                serverDate,
                serverTimestamp,
                timeDriftMs, // 关键指标：持续 > 300000(5分钟) 基本可判定为客户端时钟漂移
                isLikelyClockDrift: timeDriftMs !== null && Math.abs(timeDriftMs) > 300_000,
            });
            return e.data;
        });

    // 两个状态位同源：都以 personal_info 是否可用为准。
    // isValidWebSession 保留在返回结构里是为了兼容上游读取，值与 isValidSession 一致。
    const isValid = baseInfo?.code === 0;
    check.isValidSession = isValid;
    check.isValidWebSession = isValid;

    const message = `小红书账号有效性检测成功${_task.debug ? ` ${http.proxyInfo}` : ""}`;

    return success(check, message);
};

export const BjhSessionCheck: bjhSessionCheck = async (_task, params) => {
    const http = new Http({
        headers: {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            token: params.token,
            referer: "https://baijiahao.baidu.com/builder/rc/home",
        },
    });

    const res = await http.api({
        method: "get",
        url: "https://baijiahao.baidu.com/user-ui/cms/settingInfo",
        defaultErrorMsg: "百家号登录状态失效。",
    });

    const isSuccess = res.errno === 0;
    const message = "百家号账号有效性检测成功";
    const data = isSuccess ? { isValidSession: true } : { isValidSession: false };

    return success(data, message);
};

/**
 * 抖音创作者平台 Session 检查
 * 通过调用创作者平台用户信息接口验证登录状态
 */
export const DySessionCheck: dySessionCheck = async (_task, params) => {
    const http = new Http({
        headers: {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            referer: "https://creator.douyin.com/",
        },
    });

    // 抖音创作者平台获取用户信息的接口
    const res = await http.api<{
        status_code: number;
        data?: {
            user?: {
                uid?: string;
                nickname?: string;
            };
        };
        message?: string;
    }>({
        method: "get",
        url: "https://creator.douyin.com/aweme/v1/creator/user/info/",
        params: {
            // 抖音接口通常需要的时间戳参数
            _ts: Date.now(),
        },
        defaultErrorMsg: "抖音登录状态失效。",
    });

    // 抖音接口返回 status_code 为 0 表示成功
    const isSuccess = res.status_code === 0;
    const message = "抖音账号有效性检测成功";
    const data = isSuccess ? { isValidSession: true } : { isValidSession: false };

    return success(data, message);
};

/**
 * 视频号助手平台 Session 检查
 * 通过调用视频号助手的 auth_data 接口验证登录状态
 */
export const ShipinhaoSessionCheck: shipinhaoSessionCheck = async (_task, params) => {
    const http = new Http({
        headers: {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            referer: "https://channels.weixin.qq.com",
            "user-agent":
                "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36",
            "Content-Type": "application/json",
        },
    });

    // 获取13位时间戳
    const getTimeStamp = (length: number): string => {
        return Date.now().toString().substring(0, length);
    };

    // 构建请求体，与视频号助手保持一致
    const requestBody = {
        timestamp: getTimeStamp(13), // 13位时间戳
        _log_finder_uin: "",
        _log_finder_id: "",
        rawKeyBuff: null,
        pluginSessionId: null,
        scene: 7,
        reqScene: 7,
    };

    // 视频号助手获取用户信息的接口
    const res = await http.api<{
        errCode: number;
        errMsg?: string;
        data?: {
            finderUser?: {
                uin?: number;
                finderUsername?: string;
                nickname?: string;
                headImgUrl?: string;
            };
        };
    }>({
        method: "post",
        url: "https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/auth/auth_data",
        data: requestBody,
        defaultErrorMsg: "视频号登录状态失效。",
    });

    // 视频号接口返回 errCode 为 0 表示成功
    // errCode: 300333/300334 表示登录失效
    const isSuccess = res.errCode === 0 && res.data?.finderUser !== undefined;
    const message = isSuccess
        ? "视频号账号有效性检测成功"
        : res.errCode === 300333 || res.errCode === 300334
          ? "视频号登录已失效，请重新登录"
          : "视频号账号有效性检测失败";

    const data = isSuccess ? { isValidSession: true } : { isValidSession: false };

    return success(data, message);
};
